import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Axios } from "../../Api/axios";

/// Shared error-shape handler for every thunk below — mirrors the pattern
/// used across the rest of the app's slices, with the optional-chaining
/// fixed all the way through (no bare `.data` after `.response`).
function marketErrorPayload(error) {
  if (error?.response?.data?.error) {
    return { message: error.response.data.error };
  }
  if (error?.response?.data?.message) {
    return { message: error.response.data.message };
  }
  return { message: "Something went wrong. Please try again." };
}

// ─── Store requests (review queue) ───────────────────────────────────────────

export const fetchStoreRequests = createAsyncThunk(
  "market/fetchStoreRequests",
  async (
    { status = "pending", walletStatus = "", page = 1, limit = 10 } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await Axios.get("/market/store-requests", {
        params: {
          status,
          page,
          per_page: limit,
          ...(walletStatus ? { wallet_status: walletStatus } : {}),
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(marketErrorPayload(error));
    }
  }
);

export const fetchStoreRequestDetail = createAsyncThunk(
  "market/fetchStoreRequestDetail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/market/store-requests/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(marketErrorPayload(error));
    }
  }
);

export const approveStoreRequest = createAsyncThunk(
  "market/approveStoreRequest",
  async (id, { rejectWithValue }) => {
    try {
      const response = await Axios.post(`/market/store-requests/${id}/approve`);
      return response.data;
    } catch (error) {
      return rejectWithValue(marketErrorPayload(error));
    }
  }
);

export const declineStoreRequest = createAsyncThunk(
  "market/declineStoreRequest",
  async ({ id, reason = "" }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(`/market/store-requests/${id}/decline`, { reason });
      return response.data;
    } catch (error) {
      return rejectWithValue(marketErrorPayload(error));
    }
  }
);

/// Manually credits a payment against a store's commission wallet. If it
/// clears a negative balance, the backend reactivates the store and the
/// response reflects the new wallet_status/is_active — same store shape as
/// fetchStoreRequestDetail, so the detail page can just swap it in.
export const creditStorePayment = createAsyncThunk(
  "market/creditStorePayment",
  async ({ id, amount, note = "" }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(`/market/stores/${id}/credit-payment`, {
        amount,
        ...(note ? { note } : {}),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(marketErrorPayload(error));
    }
  }
);

// ─── Orders (cross-store) ─────────────────────────────────────────────────────

export const fetchMarketOrders = createAsyncThunk(
  "market/fetchMarketOrders",
  async ({ status = "", storeId = "", page = 1, limit = 10 } = {}, { rejectWithValue }) => {
    try {
      const response = await Axios.get("/market/admin-orders", {
        params: {
          ...(status ? { status } : {}),
          ...(storeId ? { store_id: storeId } : {}),
          page,
          per_page: limit,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(marketErrorPayload(error));
    }
  }
);

export const fetchMarketOrderDetail = createAsyncThunk(
  "market/fetchMarketOrderDetail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/market/admin-orders/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(marketErrorPayload(error));
    }
  }
);

// ─── Analytics ─────────────────────────────────────────────────────────────────

export const fetchMarketAnalytics = createAsyncThunk(
  "market/fetchMarketAnalytics",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get("/market/analytics");
      return response.data;
    } catch (error) {
      return rejectWithValue(marketErrorPayload(error));
    }
  }
);

const initialState = {
  loading: false,
  error: null,

  storeRequests: [],
  storeRequestsMeta: null,

  storeRequestDetail: null,
  storeRequestDetailLoading: false,

  approveLoading: false,
  approveSuccess: false,
  declineLoading: false,
  declineSuccess: false,

  creditPaymentLoading: false,
  creditPaymentSuccess: false,

  orders: [],
  ordersMeta: null,

  orderDetail: null,
  orderDetailLoading: false,

  analytics: null,
  analyticsLoading: false,
};

const marketSlice = createSlice({
  name: "market",
  initialState,
  reducers: {
    reset(state) {
      state.error = null;
      state.approveSuccess = false;
      state.declineSuccess = false;
      state.creditPaymentSuccess = false;
    },
    clearStoreRequestDetail(state) {
      state.storeRequestDetail = null;
    },
    clearOrderDetail(state) {
      state.orderDetail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── Store requests list ──────────────────────────────────────────────
      .addCase(fetchStoreRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStoreRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.storeRequests = action.payload?.data || [];
        state.storeRequestsMeta = action.payload?.meta || null;
      })
      .addCase(fetchStoreRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload?.message;
      })

      // ── Store request detail ─────────────────────────────────────────────
      .addCase(fetchStoreRequestDetail.pending, (state) => {
        state.storeRequestDetailLoading = true;
        state.error = null;
      })
      .addCase(fetchStoreRequestDetail.fulfilled, (state, action) => {
        state.storeRequestDetailLoading = false;
        state.storeRequestDetail = action.payload?.data || null;
      })
      .addCase(fetchStoreRequestDetail.rejected, (state, action) => {
        state.storeRequestDetailLoading = false;
        state.error = action?.payload?.message;
      })

      // ── Approve ──────────────────────────────────────────────────────────
      .addCase(approveStoreRequest.pending, (state) => {
        state.approveLoading = true;
        state.approveSuccess = false;
        state.error = null;
      })
      .addCase(approveStoreRequest.fulfilled, (state, action) => {
        state.approveLoading = false;
        state.approveSuccess = true;
        state.storeRequestDetail = action.payload?.data || state.storeRequestDetail;
      })
      .addCase(approveStoreRequest.rejected, (state, action) => {
        state.approveLoading = false;
        state.error = action?.payload?.message;
      })

      // ── Decline ──────────────────────────────────────────────────────────
      .addCase(declineStoreRequest.pending, (state) => {
        state.declineLoading = true;
        state.declineSuccess = false;
        state.error = null;
      })
      .addCase(declineStoreRequest.fulfilled, (state) => {
        state.declineLoading = false;
        state.declineSuccess = true;
      })
      .addCase(declineStoreRequest.rejected, (state, action) => {
        state.declineLoading = false;
        state.error = action?.payload?.message;
      })

      // ── Credit payment ───────────────────────────────────────────────────
      .addCase(creditStorePayment.pending, (state) => {
        state.creditPaymentLoading = true;
        state.creditPaymentSuccess = false;
        state.error = null;
      })
      .addCase(creditStorePayment.fulfilled, (state, action) => {
        state.creditPaymentLoading = false;
        state.creditPaymentSuccess = true;
        state.storeRequestDetail = action.payload?.data || state.storeRequestDetail;
      })
      .addCase(creditStorePayment.rejected, (state, action) => {
        state.creditPaymentLoading = false;
        state.error = action?.payload?.message;
      })

      // ── Orders list ──────────────────────────────────────────────────────
      .addCase(fetchMarketOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMarketOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload?.data || [];
        state.ordersMeta = action.payload?.meta || null;
      })
      .addCase(fetchMarketOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload?.message;
      })

      // ── Order detail ─────────────────────────────────────────────────────
      .addCase(fetchMarketOrderDetail.pending, (state) => {
        state.orderDetailLoading = true;
        state.error = null;
      })
      .addCase(fetchMarketOrderDetail.fulfilled, (state, action) => {
        state.orderDetailLoading = false;
        state.orderDetail = action.payload?.data || null;
      })
      .addCase(fetchMarketOrderDetail.rejected, (state, action) => {
        state.orderDetailLoading = false;
        state.error = action?.payload?.message;
      })

      // ── Analytics ────────────────────────────────────────────────────────
      .addCase(fetchMarketAnalytics.pending, (state) => {
        state.analyticsLoading = true;
        state.error = null;
      })
      .addCase(fetchMarketAnalytics.fulfilled, (state, action) => {
        state.analyticsLoading = false;
        state.analytics = action.payload?.data || null;
      })
      .addCase(fetchMarketAnalytics.rejected, (state, action) => {
        state.analyticsLoading = false;
        state.error = action?.payload?.message;
      });
  },
});

export const { reset, clearStoreRequestDetail, clearOrderDetail } = marketSlice.actions;
const marketReducer = marketSlice.reducer;

export default marketReducer;
